import { useMemo, useState, useEffect } from "react";

import {
  AccountReceivable,
  Organization,
  PortfolioResult,
  AccountReceivablesService,
  PortfolioService,
  OrganizationService,
  OrgStripeProduct,
} from "@/api";


import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  EllipsisVerticalIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { Loader2, ArrowUpDownIcon, DollarSignIcon, TrendingUpIcon, Edit, Trash2, ArrowLeftRightIcon, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { DeleteAccountReceivableConfirmation } from "./DeleteAccountReceivableConfirmation";
import { EditAccountReceivableModal } from "./EditAccountReceivableModal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";




// Helper: Currency formatter
const formatCurrency = (value: number, currency: string) => {
  const locale = currency === "NGN" ? "en-NG" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

function handleSelectAccountReceivable(
  organization: Organization,
  po: PortfolioResult,
  accountreceivable: AccountReceivable
) {
  $accountreceivable.set(accountreceivable);
  openPage($router, "accountreceivable", {
    organizationId: organization.id,
    accountreceivableId: accountreceivable.id,
    portfolioId: po.portfolio.id,
  });
}

export function renderStatusBadge(status: string) {
  let icon = null;
  let bgClass = "";
  let textColor = "";

  switch (status) {
    case "Paid":
      icon = <CheckCircleIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-green-200";
      textColor = "text-green-900";
      break;
    case "Overdue":
      icon = <ExclamationCircleIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-red-200";
      textColor = "text-red-900";
      break;
    case "Unpaid":
      icon = <ClockIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-yellow-200";
      textColor = "text-yellow-900";
      break;
    case "PartiallyPaid":
      icon = <ClockIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-purple-200";
      textColor = "text-purple-900";
      break;
    case "Cancelled":
      icon = <XMarkIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-red-200";
      textColor = "text-red-900";
      break;
    case "VoluntaryChurned":
      icon = <XMarkIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-red-200";
      textColor = "text-red-900";
      break;
    case "InvoluntaryChurned":
      icon = <XMarkIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-red-200";
      textColor = "text-red-900";
      break;
    case "VoluntaryChurnedRecovered":
      icon = <CheckCircleIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-green-200";
      textColor = "text-green-900";
      break;
    case "InvoluntaryChurnedRecovered":
      icon = <CheckCircleIcon className="h-4 w-4" strokeWidth={2.5} />;
      bgClass = "rounded-full bg-green-200";
      textColor = "text-green-900";
      break;
    default:
      bgClass = "rounded-full bg-gray-500";
      textColor = "text-white";
      break;
  }

  return (
    <Badge variant="outline" className={cn(bgClass, textColor, "text-xs")}>
      <div className="flex items-center space-x-1">
        {icon}
        <span>{status}</span>
      </div>
    </Badge>
  );
}

function formatDate(utcDate: string) {
  const date = new Date(utcDate);
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  ).toLocaleDateString();
}

function getOverdueDays(dueDate?: string | null) {
  if (!dueDate) return 0;
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}

interface ReceivablesTableProps {
  receivables: AccountReceivable[];
  sortConfig: { key: string; direction: "asc" | "desc" } | null;
  handleSort: (key: string) => void;
  org: Organization;
  po: PortfolioResult;
  handleSelectAccountReceivable: (
    org: Organization,
    po: PortfolioResult,
    accountreceivable: AccountReceivable
  ) => void;
  handleEditAccountReceivable: (accountreceivable: AccountReceivable) => void;
  setAccountReceivableToBeDeleted: (id: string) => void;
  setIsDeleteAccountReceivableModalOpen: (open: boolean) => void;

  selectMode: boolean;
  selectedIds: string[];
  setSelectMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const ReceivablesTable: React.FC<ReceivablesTableProps> = ({
  receivables,
  sortConfig,
  handleSort,
  org,
  po,
  handleSelectAccountReceivable,
  handleEditAccountReceivable,
  setAccountReceivableToBeDeleted,
  setIsDeleteAccountReceivableModalOpen,
  selectMode,
  selectedIds,
  setSelectMode,
  setSelectedIds,
}) => {
  const queryClient = useQueryClient();
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [availablePortfolios, setAvailablePortfolios] = useState<PortfolioResult[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);



  const sortedData = useMemo(() => {
    let sortableItems = [...receivables];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aVal: any;
        let bVal: any;
        switch (sortConfig.key) {
          case "name":
            aVal = a.name;
            bVal = b.name;
            break;
          case "status":
            aVal = a.status;
            bVal = b.status;
            break;
          case "invoiceDate":
            aVal = a.invoiceDate;
            bVal = b.invoiceDate;
            break;
          case "dueDate":
            aVal = a.dueDate ? new Date(a.dueDate).getTime() : 0;
            bVal = b.dueDate ? new Date(b.dueDate).getTime() : 0;
            break;
          case "balance":
            aVal = a.balance || 0;
            bVal = b.balance || 0;
            break;
          case "amountPaid":
            aVal = a.amountPaid || 0;
            bVal = b.amountPaid || 0;
            break;
          case "amountDue":
            aVal = a.amountDue || 0;
            bVal = b.amountDue || 0;
            break;
          default:
            aVal = "";
            bVal = "";
        }
        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [receivables, sortConfig]);

  const columns = [
    { header: "Client / Invoice", key: "name" },
    { header: "Product", key: "product" },
    { header: "Due Date", key: "dueDate" },
    { header: "Status", key: "status" },
    { header: "Balance", key: "balance" },
    { header: "Amount Paid", key: "amountPaid" },
    { header: "Amount Due", key: "amountDue" },
    { header: "Actions", key: null },
  ];
  const [orgStripeProducts, setOrgStripeProducts] = useState<OrgStripeProduct[]>([]);
  useEffect(() => {
    const fetchStripeProducts = async () => {
      if (org.id) {
        const products = await OrganizationService.getStripeProducts(org.id);
        setOrgStripeProducts(products);
      }
    };
    
    fetchStripeProducts();
  }, [org?.id]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelectedIds(sortedData.map((ar) => ar.id));
  const deselectAll = () => setSelectedIds([]);

  const deleteReceivablesMutation = useMutation({
    mutationFn: (ids: string[]) =>
      AccountReceivablesService.deleteMultipleAccountReceivables(ids),
    onSuccess: () => {
      toast.success("Deleted selected account receivables");
      queryClient.invalidateQueries({ queryKey: ["accountreceivables"] });
      setSelectMode(false);
      deselectAll();
    },
    onError: () => {
      toast.error("Failed to delete account receivables");
    },
  });

  return (
    <div className="overflow-x-auto w-full p-2">
      {selectMode && (
        <div className="flex items-center justify-between mb-4 bg-gray-50 px-4 py-2 border rounded">
          <span className="text-sm text-gray-700">{selectedIds.length} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={selectAll}>Select All</Button>
            <Button variant="outline" onClick={deselectAll}>Deselect All</Button>
            <Button
              variant="outline"
              disabled={selectedIds.length === 0}
              onClick={async () => {
                const portfolios = await PortfolioService.getPortfolios(org.id);
                const filtered = portfolios.filter(p => p.portfolio.id !== po.portfolio.id);
                if (filtered.length === 0) {
                  toast.warning("No other portfolio to move into.");
                  return;
                }
                setAvailablePortfolios(filtered);
                setIsMoveModalOpen(true);
              }}
            >
              Move to Portfolio
            </Button>

            <Button
              variant="outline"
              disabled={selectedIds.length === 0}
              // onClick={() => deleteReceivablesMutation.mutate(selectedIds)}
              onClick={() => setIsBulkDeleteModalOpen(true)}
            > <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected</Button>
            <Button
              variant="ghost"
              onClick={() => { deselectAll(); setSelectMode(false); }}
            >Exit</Button>
          </div>
        </div>
      )}

      <table className="w-full text-sm text-justify rounded-xl overflow-hidden">
        <thead className="text-xs uppercase text-muted-foreground bg-gray-100 text-gray-900">
          <tr>
            {selectMode && (
              <th className="px-4">
                <input
                  type="checkbox"
                  checked={selectedIds.length === sortedData.length}
                  onChange={() =>
                    selectedIds.length === sortedData.length ? deselectAll() : selectAll()
                  }
                />
              </th>
            )}
            {columns.map((col, index) =>
              col.header === "Actions" ? (
                <th key={index} className="py-3 px-4 text-left">
                  <div className="flex items-center justify-between">
                    <span>{col.header}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <EllipsisVerticalIcon className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!selectMode ? (
                          <DropdownMenuItem onClick={() => setSelectMode(true)}>
                            Enable Bulk Action
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => {
                              deselectAll();
                              setSelectMode(false);
                            }}
                          >
                            Exit Bulk Action
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </th>
              ) : (
                <th
                  key={index}
                  onClick={col.key ? () => handleSort(col.key) : undefined}
                  className={`py-3 px-4 ${col.key ? "cursor-pointer hover:bg-gray-200" : "cursor-default"
                    } border-none text-left`}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.key && sortConfig?.key === col.key && (
                      <ArrowUpDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </th>
              )
            )}
          </tr>
        </thead>

        <tbody>
          {sortedData.map(ar => {
            const overdueDays = getOverdueDays(ar.dueDate);
            const stripeProduct = orgStripeProducts.find(p => p.stripeProductId === ar.stripeProductId);
            return (
              <tr key={ar.id} className="group hover:bg-gray-100 transition-colors border">
                {selectMode && (
                  <td className="px-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(ar.id)}
                      onChange={() => toggleSelect(ar.id)}
                    />
                  </td>
                )}
                <td className="py-3 px-4 font-medium text-lg">
                  <div className="rounded p-1">
                    <a
                      onClick={() => handleSelectAccountReceivable(org, po, ar)}
                      className="text-gray-900 group-hover:text-blue-600 transition-colors cursor-pointer"
                    // >{ar.name}</a>
                    >
                      {ar.customer.contactEmail ?? ar.name}
                    </a>
                    {ar.loanId && (
                      <div className="text-xs text-muted-foreground mt-1">
                        INV-{ar.loanId}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm text-gray-900">
                    {stripeProduct?.name ?? 'N/A'}
                  </div>
                </td>
                <td className="py-3 px-4 text-sm">
                  <div className="flex flex-col">
                    {ar.dueDate && <span className="text-base font-sans">{formatDate(ar.dueDate)}</span>}
                    {ar.invoiceDate && (
                      <span className="text-xs text-muted-foreground mt-1">
                        Issued: {formatDate(ar.invoiceDate)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-auto">
                  <div className="flex flex-col gap-1">
                    {renderStatusBadge(ar.status)}
                    {ar.status !== "Paid" && overdueDays > 0 && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-100 text-xs px-2 py-1 flex items-center rounded-full whitespace-nowrap">
                        <span className="font-semibold">{overdueDays}</span><span className="ml-1">days overdue</span>
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-sm">
                  {ar.balance !== undefined ? formatCurrency(ar.balance, org.name === "Sanaam" ? "NGN" : "USD") : "N/A"}
                </td>
                <td className="py-3 px-4 text-sm">
                  {ar.amountPaid !== undefined ? formatCurrency(ar.amountPaid, org.name === "Sanaam" ? "NGN" : "USD") : "N/A"}
                </td>
                <td className="py-3 px-4 text-sm">
                  {ar.amountDue !== undefined ? formatCurrency(ar.amountDue, org.name === "Sanaam" ? "NGN" : "USD") : "N/A"}
                </td>
                <td className="w-[1%] whitespace-nowrap py-3 px-4 text-right align-middle">
                  <div className="rounded p-1 flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" onClick={() => handleSelectAccountReceivable(org, po, ar)}>
                      <EyeIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEditAccountReceivable(ar)}>
                      <Edit className="h-5 w-5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <EllipsisVerticalIcon className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleEditAccountReceivable(ar)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSelectAccountReceivable(org, po, ar)}>
                          <EyeIcon className="w-4 h-4 mr-2" />
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => { setAccountReceivableToBeDeleted(ar.id); setIsDeleteAccountReceivableModalOpen(true); }}>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={async () => {
                            const portfolios = await PortfolioService.getPortfolios(org.id);
                            const filtered = portfolios.filter(p => p.portfolio.id !== po.portfolio.id);
                            if (filtered.length === 0) {
                              toast.warning("No other portfolio to move into.");
                              return;
                            }
                            setAvailablePortfolios(filtered);
                            setSelectedPortfolioId(null);
                            setSelectedIds([ar.id]);
                            setIsMoveModalOpen(true);
                          }}
                        >
                          <ArrowLeftRightIcon className="w-4 h-4 mr-2" />
                          Move
                        </DropdownMenuItem>

                        {!selectMode && (
                          <DropdownMenuItem onClick={() => setSelectMode(true)}>
                            <ListChecks className="w-4 h-4 mr-2" />
                            Bulk Action
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Dialog open={isMoveModalOpen} onOpenChange={setIsMoveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Receivables</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a portfolio to move <strong>{selectedIds.length}</strong> receivable(s) into:
            </p>
            <Select onValueChange={setSelectedPortfolioId}>
              <SelectTrigger>
                <SelectValue placeholder="Select portfolio" />
              </SelectTrigger>
              <SelectContent>
                {availablePortfolios.map(p => (
                  <SelectItem key={p.portfolio.id} value={p.portfolio.id}>
                    {p.portfolio.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsMoveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!selectedPortfolioId}
              onClick={async () => {
                if (!selectedPortfolioId) {
                  toast.error("Please select a portfolio.");
                  return;
                }
                try {
                  await AccountReceivablesService.moveAccountReceivables({
                    accountReceivableIds: selectedIds,
                    newPortfolioId: selectedPortfolioId,
                  });
                  toast.success("Receivables moved successfully");
                  queryClient.invalidateQueries({ queryKey: ["accountreceivables"] });

                  // ✅ Refresh server-side data too
                  queryClient.invalidateQueries({ queryKey: ["accountreceivables"] });
                  setIsMoveModalOpen(false);
                  setSelectMode(false);
                  setSelectedIds([]);
                } catch (e) {
                  toast.error("Failed to move receivables");
                }
              }}
            >
              Confirm Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-md shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-2">
              Confirm Deletion
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete {selectedIds.length} selected account receivables? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsBulkDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  deleteReceivablesMutation.mutate(selectedIds);
                  setIsBulkDeleteModalOpen(false);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface AccountReceivableListingProps {
  viewMode?: "table";
}

export const AccountReceivableListing = ({ viewMode = "table" }: AccountReceivableListingProps) => {
  const org = useStore($org);
  const po = useStore($portfolio);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: accountreceivables, isLoading: loadingAccountReceivables } = useQuery({
    queryKey: ['accountreceivables', org?.id, po?.portfolio.id, currentPage, pageSize],
    queryFn: async () => {
      if (!org?.id || !po?.portfolio.id) return {
        items: [],
        totalCount: 0,
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false
      };
      const response = await AccountReceivablesService.getAccountReceivables(
        po.portfolio.id,
        currentPage,
        pageSize
      );
      return response ?? {
        items: [],
        totalCount: 0,
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false
      };
    },
    enabled: !!org?.id && !!po?.portfolio.id,
    placeholderData: (previousData) => previousData,
  });

  const totalPages = accountreceivables?.totalPages ?? 1;
  const totalItems = accountreceivables?.totalCount ?? 0;

  // For delete modal
  const [isDeleteAccountReceivableModalOpen, setIsDeleteAccountReceivableModalOpen] = useState(false);
  const [accountReceivableToBeDeleted, setAccountReceivableToBeDeleted] = useState("");

  // For edit modal
  const [updateAccountReceivableModalOpen, setUpdateAccountReceivableModalOpen] = useState(false);
  const [selectedAccountReceivable, setSelectedAccountReceivable] = useState<any | null>(null);

  // Sorting state for table columns
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

  const queryClient = useQueryClient();


  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);


  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleEditAccountReceivable = (accountreceivable: any) => {
    setSelectedAccountReceivable({
      id: accountreceivable.id,
      name: accountreceivable.name,
      loanId: accountreceivable.loanId,
      portfolioId: accountreceivable.portfolioId,
      customer: accountreceivable.customer,
      balance: accountreceivable.balance,
      invoiceDate: new Date(accountreceivable.invoiceDate),
      dueDate: new Date(accountreceivable.dueDate),
      amountPaid: accountreceivable.amountPaid,
      amountDue: accountreceivable.amountDue,
      accountReceivableDataMedium: accountreceivable.accountReceivableDataMedium,
    });
    setUpdateAccountReceivableModalOpen(true);
  };

  // Sort all receivables by createdAt descending
  const sortedAccountReceivables = accountreceivables?.items?.slice()
    .sort(
      (a, b) =>
        new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime()
    ) ?? [];

  // Subsets for tabs
  const allReceivables = sortedAccountReceivables;
  const pendingReceivables: AccountReceivable[] = [];
  const overdueReceivables = sortedAccountReceivables.filter(
    (ar) =>
      ar.status === "Overdue" ||
      ar.status === "PartiallyPaid" ||
      ar.status === "Unpaid"
  );
  const paidReceivables = sortedAccountReceivables.filter(
    (ar) => ar.status === "Paid"
  );

  const totalUnpaid = accountreceivables?.items?.filter(a => a.status !== 'VoluntaryChurned')
    .reduce(
      (acc, a) => acc + ((a.balance || 0) - (a.amountPaid || 0)),
      0
    ) ?? 0;

  const totalRecovered = accountreceivables?.items?.filter(a => a.status !== 'VoluntaryChurned')
    .reduce(
      (acc, a) => acc + (a.amountPaid || 0),
      0
    ) ?? 0;

  const totalOverdue = accountreceivables?.items?.filter((a) => a.status === "Overdue")
    .reduce((acc, a) => acc + (a.amountDue || 0), 0) ?? 0;

  const totalCollected = accountreceivables?.items?.filter((a) => {
      const invoiceDate = new Date(a.invoiceDate ?? '');
      const now = new Date();
      return (
        a.status === "Paid" &&
        invoiceDate.getMonth() === now.getMonth() &&
        invoiceDate.getFullYear() === now.getFullYear()
      );
    })
    .reduce((acc, a) => acc + (a.amountPaid || 0), 0) ?? 0;

  const paidInvoices = accountreceivables?.items?.filter(
    (a) => a.status === "Paid" && a.invoiceDate && a.dueDate
  ) ?? [];

  const totalDays = paidInvoices.reduce((acc, a) => {
    const invoiceDate = new Date(a.invoiceDate ?? '');
    const dueDate = new Date(a.dueDate ?? '');
    const diffDays = Math.round(
      (dueDate.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    return acc + diffDays;
  }, 0);

  const avgPaymentTime = paidInvoices.length
    ? Math.round(totalDays / paidInvoices.length)
    : 0;
  // // Sample data for monthly collections chart
  // const monthlyCollectionData = [
  //   { name: "Jan", amount: 12000 },
  //   { name: "Feb", amount: 14000 },
  //   { name: "Mar", amount: 11000 },
  //   { name: "Apr", amount: 16500 },
  //   { name: "May", amount: 18000 },
  //   { name: "Jun", amount: 19500 },
  // ];

  

  if (loadingAccountReceivables) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!accountreceivables?.items || accountreceivables.items.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p>No accounts found.</p>
      </div>
    );
  }

  if (!org || !po) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p>Loading organization/portfolio data...</p>
      </div>
    );
  }

  return (
    <>
      {/* Dashboard Stats */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 mb-6 lg:grid-cols-2 ">
        <div className="bg-white p-4 rounded-xl shadow-lg relative ">
          <div className="absolute top-2 right-2 p-2 bg-gray-100 rounded">
            <DollarSignIcon className="h-5 w-5 text-blue-500" />
          </div>
          <div className="pl-2">
            <p className="text-sm text-gray-500">Total Unpaid</p>
            <p className="text-2xl font-semibold">{formatCurrency(totalUnpaid)}</p>
          </div>
        </div> */}
      {/* Overdue Invoices */}
      {/* <div className="bg-white p-4 rounded shadow-lg relative">
          <div className="absolute top-2 right-2">
            <AlertTriangleIcon className="h-6 w-6 text-orange-500" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Average Day Sales Outstanding</p>
            <p className="text-2xl font-semibold">
              {formatCurrency(totalOverdue)}
            </p>
          </div>
        </div> */}
      {/* Collected This Month */}
      {/* <div className="bg-white p-4 rounded-xl shadow-lg relative">
          <div className="absolute top-2 right-2 p-2 bg-gray-100 rounded">
            <TrendingUpIcon className="h-5 w-5 text-green-500" />
          </div>
          <div className="pl-2">
            <p className="text-sm text-gray-500">Amount Recovered</p>
            <p className="text-2xl font-semibold">{formatCurrency(totalRecovered)}</p>
          </div>
        </div> */}
      {/* Avg. Payment Time */}
      {/* <div className="bg-white p-4 rounded shadow-lg relative">
          <div className="absolute top-2 right-2">
            <CalendarIcon className="h-6 w-6 text-purple-500" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Avg. Payment Time</p>
            <p className="text-2xl font-semibold">{avgPaymentTime} days</p>
          </div>
        </div> */}
      {/* </div> */}


      {/* Tabbed Table View */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-4 rounded shadow-lg">
          <h2 className="text-lg font-medium mb-2">Monthly Collections</h2>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyCollectionData}
                margin={{ top: 10, right: 10, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#71717A" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#71717A" }}
                  tickFormatter={(value) => `$${value / 1000}k`}
                />
                <Tooltip
                  formatter={(value) => [`$${value}`, "Amount"]}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #E4E4E7",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                  }}
                />
                <Bar
                  dataKey="amount"
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                  barSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-4 rounded shadow-lg">
          <h2 className="text-lg font-medium mb-2">Collection Performance</h2>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Collection Rate</span>
              <span className="text-sm font-semibold">78%</span>
            </div>
            <div className="w-full bg-gray-100 h-2 rounded-full mb-1 overflow-hidden">
              <div className="bg-blue-500 h-2" style={{ width: "78%" }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Target: 85%</span>
              <span>Needs improvement</span>
            </div>
          </div>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">
                AI Reminder Success Rate
              </span>
              <span className="text-sm font-semibold">76%</span>
            </div>
            <div className="w-full bg-gray-100 h-2 rounded-full mb-1 overflow-hidden">
              <div className="bg-green-500 h-2" style={{ width: "76%" }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Industry avg: 62%</span>
              <span>Above average</span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">
                Days Sales Outstanding (DSO)
              </span>
              <span className="text-sm font-semibold">32 days</span>
            </div>
            <div className="w-full bg-gray-100 h-2 rounded-full mb-1 overflow-hidden">
              <div className="bg-orange-500 h-2" style={{ width: "54%" }} />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Target: 30 days</span>
              <span>Slightly high</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Dashboard Stats Container */}
      <div className=" p-4 rounded-xl mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {/* Total Unpaid Card */}
          <div className="bg-white border p-4 rounded-xl shadow-lg relative">
            <div className="absolute top-2 right-2 p-2 bg-gray-100 rounded">
              <DollarSignIcon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="pl-2">
              <p className="text-sm text-gray-500">Total Unpaid</p>
              {org.name === "Sanaam" ? (
                <p className="text-2xl font-semibold">{formatCurrency(totalUnpaid, "NGN")}</p>
              ) : (
                <p className="text-2xl font-semibold">{formatCurrency(totalUnpaid, "USD")}</p>
              )}
            </div>
          </div>

          {/* Amount Recovered Card */}
          <div className="bg-white border p-4 rounded-xl shadow-lg relative">
            <div className="absolute top-2 right-2 p-2 bg-gray-100 rounded">
              <TrendingUpIcon className="h-5 w-5 text-green-500" />
            </div>
            <div className="pl-2">
              <p className="text-sm text-gray-500">Amount Recovered</p>
              {org.name === "Sanaam" ? (
                <p className="text-2xl font-semibold">{formatCurrency(totalRecovered, "NGN")}</p>
              ) : (
                <p className="text-2xl font-semibold">{formatCurrency(totalRecovered, "USD")}</p>
              )}
            </div>
          </div>
        </div>


        <section className="mt-6 rounded-md shadow w-full">
          <Tabs defaultValue="all" className="w-full">
            <div className="px-4 pt-4">
              <TabsList className="flex w-full">
                <TabsTrigger value="all" className="flex-1 text-center">
                  All ({allReceivables.length})
                </TabsTrigger>
                <TabsTrigger value="pending" className="flex-1 text-center">
                  Pending ({pendingReceivables.length})
                </TabsTrigger>
                <TabsTrigger value="overdue" className="flex-1 text-center">
                  Overdue ({overdueReceivables.length})
                </TabsTrigger>
                <TabsTrigger value="paid" className="flex-1 text-center">
                  Paid ({paidReceivables.length})
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="all">
              <ReceivablesTable
                receivables={allReceivables}
                sortConfig={sortConfig}
                handleSort={handleSort}
                org={org}
                po={po}
                handleSelectAccountReceivable={handleSelectAccountReceivable}
                handleEditAccountReceivable={handleEditAccountReceivable}
                setAccountReceivableToBeDeleted={setAccountReceivableToBeDeleted}
                setIsDeleteAccountReceivableModalOpen={setIsDeleteAccountReceivableModalOpen}
                selectMode={selectMode}
                selectedIds={selectedIds}
                setSelectMode={setSelectMode}
                setSelectedIds={setSelectedIds}
              />
            </TabsContent>
            <TabsContent value="pending">
              <ReceivablesTable
                receivables={pendingReceivables}
                sortConfig={sortConfig}
                handleSort={handleSort}
                org={org}
                po={po}
                handleSelectAccountReceivable={handleSelectAccountReceivable}
                handleEditAccountReceivable={handleEditAccountReceivable}
                setAccountReceivableToBeDeleted={setAccountReceivableToBeDeleted}
                setIsDeleteAccountReceivableModalOpen={setIsDeleteAccountReceivableModalOpen}
                selectMode={selectMode}
                selectedIds={selectedIds}
                setSelectMode={setSelectMode}
                setSelectedIds={setSelectedIds}
              />
            </TabsContent>
            <TabsContent value="overdue">
              <ReceivablesTable
                receivables={overdueReceivables}
                sortConfig={sortConfig}
                handleSort={handleSort}
                org={org}
                po={po}
                handleSelectAccountReceivable={handleSelectAccountReceivable}
                handleEditAccountReceivable={handleEditAccountReceivable}
                setAccountReceivableToBeDeleted={setAccountReceivableToBeDeleted}
                setIsDeleteAccountReceivableModalOpen={setIsDeleteAccountReceivableModalOpen}
                selectMode={selectMode}
                selectedIds={selectedIds}
                setSelectMode={setSelectMode}
                setSelectedIds={setSelectedIds}
              />
            </TabsContent>
            <TabsContent value="paid">
              <ReceivablesTable
                receivables={paidReceivables}
                sortConfig={sortConfig}
                handleSort={handleSort}
                org={org}
                po={po}
                handleSelectAccountReceivable={handleSelectAccountReceivable}
                handleEditAccountReceivable={handleEditAccountReceivable}
                setAccountReceivableToBeDeleted={setAccountReceivableToBeDeleted}
                setIsDeleteAccountReceivableModalOpen={setIsDeleteAccountReceivableModalOpen}
                selectMode={selectMode}
                selectedIds={selectedIds}
                setSelectMode={setSelectMode}
                setSelectedIds={setSelectedIds}
              />
            </TabsContent>
          </Tabs>
        </section>

        {/* Add pagination controls */}
        <div className="mt-4 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
            </p>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
            >
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder="10" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteAccountReceivableConfirmation
          isOpen={isDeleteAccountReceivableModalOpen}
          handleOpen={setIsDeleteAccountReceivableModalOpen}
          accountreceivableId={accountReceivableToBeDeleted}
        />

        {/* Edit Modal Integration */}
        {selectedAccountReceivable && (
          <EditAccountReceivableModal
            isOpen={updateAccountReceivableModalOpen}
            setOpen={(open) => {
              setUpdateAccountReceivableModalOpen(open);
              if (!open) {
                setSelectedAccountReceivable(null);
              }
            }}
            editingAccountReceivable={selectedAccountReceivable}
          />
        )}


      </div>
    </>
  );
};
